import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import propertyViewingsService from "../services/propertyViewingsService";
import Loader from "../components/Loader";
import styles from "./PropertyViewingsCreate.module.css";

const PropertyViewingsCreate = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Form data
  const [formData, setFormData] = useState({
    user_id: "", // No pre-selection - user must select
    guardians: [],
    viewings: [], // Start with empty viewings - user must add properties
  });

  // Available users for dropdown
  const [users, setUsers] = useState({});

  // Typeahead data
  const [properties, setProperties] = useState([]);
  const [guardians, setGuardians] = useState([]);
  const [propertySearchTerm, setPropertySearchTerm] = useState(""); // No pre-fill
  const [guardianSearchTerm, setGuardianSearchTerm] = useState("");

  // Form errors
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setLoading(true);
        console.log(
          "🔍 [FRONTEND] Fetching users for property viewings create"
        );

        // Fetch users for dropdown (matching Laravel structure)
        const usersResponse =
          await propertyViewingsService.getUsersForPropertyViewings();
        console.log("📊 [FRONTEND] Users response:", usersResponse);
        console.log("📊 [FRONTEND] Users response.data:", usersResponse.data);
        console.log("📊 [FRONTEND] Users response.data.success:", usersResponse.data?.success);
        console.log("📊 [FRONTEND] Users response.data.data:", usersResponse.data?.data);

        // Handle response structure: backend returns { success: true, data: { 1: "Name", ... } }
        // Axios wraps it: response.data = { success: true, data: { 1: "Name", ... } }
        let usersData = {};
        if (usersResponse.data) {
          if (usersResponse.data.success && usersResponse.data.data) {
            // Standard response structure: { success: true, data: users }
            usersData = usersResponse.data.data;
          } else if (usersResponse.data.data && typeof usersResponse.data.data === 'object') {
            // Direct data structure: { data: users }
            usersData = usersResponse.data.data;
          } else if (typeof usersResponse.data === 'object' && !usersResponse.data.success) {
            // Direct users object: { 1: "Name", ... }
            usersData = usersResponse.data;
          }
        }
        
        console.log("📊 [FRONTEND] Processed users data:", usersData);
        console.log("📊 [FRONTEND] Users count:", Object.keys(usersData).length);
        setUsers(usersData);

        setLoading(false);
      } catch (err) {
        console.error("❌ [FRONTEND] Error fetching initial data:", err);
        // No fallback - only use live database data
        setUsers({});
        setLoading(false);
      }
    };

    fetchInitialData();
  }, []);

  // Fetch properties for typeahead
  useEffect(() => {
    const fetchProperties = async () => {
      if (propertySearchTerm.length >= 2) {
        try {
          console.log(
            "🔍 [FRONTEND] Searching properties:",
            propertySearchTerm
          );

          // Real property search API call
          const propertiesResponse = await propertyViewingsService.searchProperties(propertySearchTerm);
          if (propertiesResponse.data && propertiesResponse.data.success) {
            setProperties(propertiesResponse.data.data || []);
          } else {
            setProperties([]);
          }
        } catch (err) {
          console.error("❌ [FRONTEND] Error searching properties:", err);
          setProperties([]);
        }
      } else {
        setProperties([]);
      }
    };

    fetchProperties();
  }, [propertySearchTerm]);

  // Debug: Track guardians state changes
  useEffect(() => {
    console.log("🔄 [STATE] Guardians state changed:", {
      count: guardians.length,
      guardians: guardians,
      searchTerm: guardianSearchTerm,
      guardiansDetails: guardians.map(g => ({ id: g.id, name: g.name, email: g.email }))
    });
  }, [guardians, guardianSearchTerm]);

  // Fetch guardians for typeahead
  useEffect(() => {
    const fetchGuardians = async () => {
      if (guardianSearchTerm.length >= 2) {
        try {
          console.log(
            "🔍 [FRONTEND] Searching guardians:",
            guardianSearchTerm
          );

          // Real guardian search API call
          const guardiansResponse = await propertyViewingsService.searchGuardians(guardianSearchTerm);
          
          console.log("📦 [FRONTEND] Full API response:", guardiansResponse);
          console.log("📦 [FRONTEND] Response status:", guardiansResponse?.status);
          console.log("📦 [FRONTEND] Response data:", guardiansResponse?.data);
          console.log("📦 [FRONTEND] Response data type:", typeof guardiansResponse?.data);
          console.log("📦 [FRONTEND] Is array?", Array.isArray(guardiansResponse?.data));
          
          // Backend returns direct array: res.json(guardians)
          // Axios wraps it: response.data = [guardians array]
          let guardiansData = [];
          
          // Extract data from axios response
          const responseData = guardiansResponse?.data;
          
          // Handle direct array response (expected format)
          if (Array.isArray(responseData)) {
            guardiansData = responseData;
            console.log("✅ [FRONTEND] Direct array response detected, count:", guardiansData.length);
            if (guardiansData.length > 0) {
              console.log("✅ [FRONTEND] First guardian in response:", guardiansData[0]);
            }
          } 
          // Handle wrapped response formats (fallback)
          else if (responseData && typeof responseData === 'object') {
            if (Array.isArray(responseData.data)) {
              guardiansData = responseData.data;
              console.log("✅ [FRONTEND] Wrapped response.data detected, count:", guardiansData.length);
            } else if (Array.isArray(responseData.guardians)) {
              guardiansData = responseData.guardians;
              console.log("✅ [FRONTEND] Wrapped response.guardians detected, count:", guardiansData.length);
            } else {
              console.warn("⚠️ [FRONTEND] Response is object but no array found");
              console.warn("⚠️ [FRONTEND] Response keys:", Object.keys(responseData || {}));
              console.warn("⚠️ [FRONTEND] Full response:", JSON.stringify(responseData, null, 2));
            }
          } 
          // Handle unexpected formats
          else {
            console.warn("⚠️ [FRONTEND] Unexpected response format");
            console.warn("⚠️ [FRONTEND] Response data:", responseData);
            console.warn("⚠️ [FRONTEND] Response data type:", typeof responseData);
          }
          
          console.log("✅ [FRONTEND] Final guardiansData count:", guardiansData.length);

          // Map guardians to expected format with name and email
          // Backend structure: { id, unique_guardian_number, status_id, user: { first_name, last_name, email, mobile_number } }
          const mappedGuardians = guardiansData.map((guardian) => {
            // Handle nested user object structure from backend
            const user = guardian.user || {};
            const firstName = user.first_name || guardian.first_name || "";
            const lastName = user.last_name || guardian.last_name || "";
            const email = user.email || guardian.email || "";
            const fullName = `${firstName} ${lastName}`.trim() || "Unknown Guardian";

            console.log("🔍 [MAPPING] Mapping guardian:", {
              id: guardian.id,
              firstName,
              lastName,
              email,
              fullName,
              hasUser: !!guardian.user,
              userObject: guardian.user
            });

            return {
              id: guardian.id,
              name: fullName,
              email: email,
              unique_guardian_number: guardian.unique_guardian_number || "",
              status_id: guardian.status_id || null,
            };
          });

          console.log("✅ [FRONTEND] Mapped guardians count:", mappedGuardians.length);
          console.log("✅ [FRONTEND] Mapped guardians:", mappedGuardians);
          
          if (mappedGuardians.length > 0) {
            console.log("✅ [FRONTEND] First mapped guardian:", mappedGuardians[0]);
          }
          
          // Set state - this will trigger re-render
          setGuardians(mappedGuardians);
          console.log("✅ [FRONTEND] State updated with", mappedGuardians.length, "guardians");
        } catch (err) {
          console.error("❌ [FRONTEND] Error searching guardians:", err);
          setGuardians([]);
        }
      } else {
        setGuardians([]);
      }
    };

    fetchGuardians();
  }, [guardianSearchTerm]);

  const handleUserChange = (e) => {
    const { value } = e.target;
    setFormData((prev) => ({
      ...prev,
      user_id: value,
    }));

    // Clear error when user starts typing
    if (errors.user_id) {
      setErrors((prev) => ({
        ...prev,
        user_id: "",
      }));
    }
  };

  const handlePropertySearch = (e) => {
    const { value } = e.target;
    setPropertySearchTerm(value);
  };

  const handlePropertySelect = (property) => {
    // Add new viewing with selected property
    const newViewing = {
      property_id: property.id,
      property_name: property.name,
      day: "",
      time_from: "",
      time_to: "",
    };

    setFormData((prev) => ({
      ...prev,
      viewings: [...prev.viewings, newViewing],
    }));

    setPropertySearchTerm("");
    setProperties([]);
  };

  const handleGuardianSearch = (e) => {
    const { value } = e.target;
    setGuardianSearchTerm(value);
  };

  const handleGuardianSelect = (guardian) => {
    // Check if guardian is already added
    const isAlreadyAdded = formData.guardians.some(
      (g) => g.guardian_id === guardian.id
    );

    if (isAlreadyAdded) {
      console.log("⚠️ Guardian already added:", guardian.name);
      // Clear search but don't add duplicate
      setGuardianSearchTerm("");
      setGuardians([]);
      return;
    }

    // Add guardian to the list (Laravel style)
    const newGuardian = {
      guardian_id: guardian.id,
      guardian_name: guardian.name,
      guardian_email: guardian.email,
    };

    setFormData((prev) => ({
      ...prev,
      guardians: [...prev.guardians, newGuardian],
    }));

    // Clear search input to allow adding more guardians
    setGuardianSearchTerm("");
    setGuardians([]);
    
    console.log("✅ Guardian added:", guardian.name);
    console.log("✅ Total guardians:", formData.guardians.length + 1);
  };

  const handleViewingChange = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      viewings: prev.viewings.map((viewing, i) =>
        i === index ? { ...viewing, [field]: value } : viewing
      ),
    }));

    // Clear error when user starts typing
    if (errors[`viewings.${index}.${field}`]) {
      setErrors((prev) => ({
        ...prev,
        [`viewings.${index}.${field}`]: "",
      }));
    }
  };

  const removeViewing = (index) => {
    setFormData((prev) => ({
      ...prev,
      viewings: prev.viewings.filter((_, i) => i !== index),
    }));
  };

  const removeGuardian = (index) => {
    setFormData((prev) => ({
      ...prev,
      guardians: prev.guardians.filter((_, i) => i !== index),
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.user_id) {
      newErrors.user_id = "User is required";
    }

    if (formData.viewings.length === 0) {
      newErrors.viewings = "Please add at least one property viewing";
    }

    formData.viewings.forEach((viewing, index) => {
      if (!viewing.property_id) {
        newErrors[`viewings.${index}.property_id`] = "Property is required";
      }
      if (!viewing.day) {
        newErrors[`viewings.${index}.day`] = "Day is required";
      }
      if (!viewing.time_from) {
        newErrors[`viewings.${index}.time_from`] = "Time from is required";
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      setError("Please fix the errors before submitting");
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      setSuccess(null);

      console.log("🚀 [FRONTEND] Submitting viewings data:", formData);
      console.log("🔍 [FRONTEND] Viewings details:", JSON.stringify(formData.viewings.map((v, i) => ({
        index: i,
        property_id: v.property_id,
        property_name: v.property_name,
        day: v.day,
        time_from: v.time_from,
        time_to: v.time_to
      })), null, 2));

      // Transform formData to match backend expectations
      const requestData = {
        user_id: formData.user_id,
        guardian_id: formData.guardians.length > 0 ? formData.guardians[0].guardian_id : null,
        viewings: formData.viewings
      };

      console.log("🔍 [FRONTEND] Transformed request data:", requestData);

      // Call the storeMany endpoint (matching Laravel route)
      const response = await propertyViewingsService.createViewings(requestData);

      if (response.data && response.data.success === true) {
        setSuccess(response.data.message || "Viewings have been added!");
        console.log("✅ [FRONTEND] Viewings created successfully");
        setTimeout(() => {
          navigate("/property_viewings");
        }, 2000);
      } else {
        throw new Error(response.data?.message || "Failed to create viewings");
      }
    } catch (err) {
      console.error("❌ [FRONTEND] Error creating viewings:", err);
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "Failed to create viewings";
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <div className={styles.container}>
      {/* Breadcrumbs */}
      <div className={styles.breadcrumbRow}>
        <span className={styles.breadcrumbMain} onClick={() => navigate('/dashboard')} style={{ cursor: 'pointer', color: '#337ab7' }}>Dashboard</span>
        <div className={styles.breadcrumbCurrent}>
          <span className={styles.breadcrumbSep}>/</span>
          <span className={styles.breadcrumbProperties}>Property Viewings</span>
        </div>
      </div>

      <div className={styles.mainContent}>
        <div className={styles.panelForm}>
          <div className={styles.panelHeading}>
            <h3>Create Property Viewings</h3>
          </div>
          <div className={styles.panelBody}>
            <form onSubmit={handleSubmit}>
              <fieldset className={styles.formHorizontal}>
                {/* User Section */}
                <div className={styles.reportSectionHeader}>User</div>
                <div className={styles.formGroup}>
                  <label className={`${styles.controlLabel} ${styles.labelRequired}`}>
                    User
                  </label>
                  <div className={styles.colSm5}>
                    <select
                      name="user_id"
                      className={styles.formControl}
                      value={formData.user_id}
                      onChange={handleUserChange}
                      required
                    >
                      <option value="">Please select</option>
                      {Object.entries(users).map(([id, name]) => (
                        <option key={id} value={id}>
                          {name}
                        </option>
                      ))}
                    </select>
                    {errors.user_id && (
                      <div className={styles.textDanger}>
                        {errors.user_id}
                      </div>
                    )}
                  </div>
                </div>

                {/* Guardians Section */}
                <div className={styles.reportSectionHeader}>Guardians</div>
                <div className={styles.formGroup}>
                  <label className={styles.controlLabel}>
                    Type guardian's email or name to add
                  </label>
                  <div className={styles.colSm5} style={{ position: 'relative' }}>
                    <input
                      className={styles.formControl}
                      id="guardian_title"
                      name="guardian_title"
                      type="text"
                      value={guardianSearchTerm}
                      onChange={handleGuardianSearch}
                      data-guardian="0"
                      autoComplete="off"
                    />
                    {/* Guardian Typeahead Dropdown */}
                    {(() => {
                      const shouldShow = guardianSearchTerm.length >= 2;
                      console.log("🎨 [RENDER] Dropdown render check:", {
                        shouldShow,
                        searchTerm: guardianSearchTerm,
                        guardiansCount: guardians.length,
                        guardians: guardians
                      });
                      
                      if (!shouldShow) return null;
                      
                      return (
                        <div 
                          className={styles.typeaheadDropdown} 
                          style={{ 
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            right: 0,
                            zIndex: 10000,
                            backgroundColor: 'white',
                            border: '1px solid #ccc',
                            borderTop: 'none',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                            maxHeight: '200px',
                            overflowY: 'auto',
                            display: 'block',
                            visibility: 'visible',
                            marginTop: '2px',
                            width: '100%'
                          }}
                          onMouseDown={(e) => e.preventDefault()}
                        >
                          {guardians.length > 0 ? (
                            <>
                              {console.log("🎨 [RENDER] Rendering", guardians.length, "guardians")}
                              {guardians.map((guardian) => {
                                const displayName = guardian.name || 'Unknown Guardian';
                                const displayEmail = guardian.email || 'No email';
                                
                                console.log("🎨 [RENDER] Rendering guardian item:", guardian.id, displayName, displayEmail);
                                
                                return (
                                  <div
                                    key={guardian.id || `guardian-${guardian.unique_guardian_number || Math.random()}`}
                                    className={styles.typeaheadItem}
                                    onClick={() => {
                                      console.log("🖱️ [CLICK] Guardian selected:", guardian);
                                      handleGuardianSelect(guardian);
                                    }}
                                    onMouseDown={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                    }}
                                    style={{
                                      cursor: 'pointer',
                                      padding: '8px 12px',
                                      borderBottom: '1px solid #eee',
                                      backgroundColor: 'white'
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.backgroundColor = '#f5f5f5';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.backgroundColor = 'white';
                                    }}
                                  >
                                    <strong>{displayName}</strong> ({displayEmail})
                                  </div>
                                );
                              })}
                            </>
                          ) : (
                            <div 
                              className={styles.typeaheadItem} 
                              style={{ 
                                padding: '10px', 
                                color: '#666', 
                                fontStyle: 'italic',
                                cursor: 'default',
                                borderBottom: 'none'
                              }}
                            >
                              No guardians found
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                </div>

                {/* Selected Guardians List - Laravel Style */}
                {formData.guardians.length > 0 && (
                  <div className={styles.guardians}>
                    {formData.guardians.map((guardian, index) => (
                      <div key={guardian.guardian_id || index} className={styles.guardianItem}>
                        <span className={styles.guardianName}>
                          {guardian.guardian_name}
                        </span>
                        <button
                          type="button"
                          className={styles.btnDelete}
                          onClick={() => removeGuardian(index)}
                          title="Delete guardian"
                        >
                          Delete
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Viewings Section */}
                <div className={styles.reportSectionHeader}>Viewings</div>
                <div className={styles.formGroup}>
                  <label
                    htmlFor="new_field_input"
                    className={`${styles.controlLabel} ${styles.labelRequired}`}
                  >
                    Type property ID or name (or *)
                  </label>
                  <div className={styles.colSm5}>
                    <input
                      id="new_field_input"
                      className={`${styles.formControl} ${styles.jsPropertyFieldTypeahead}`}
                      type="text"
                      data-index="0"
                      value={propertySearchTerm}
                      onChange={handlePropertySearch}
                      placeholder="Type property ID or name (or *)"
                    />
                    {properties.length > 0 && (
                      <div className={styles.typeaheadDropdown}>
                        {properties.map((property) => (
                          <div
                            key={property.id}
                            className={styles.typeaheadItem}
                            onClick={() => handlePropertySelect(property)}
                          >
                            {property.name} ({property.postcode})
                          </div>
                        ))}
                      </div>
                    )}
                    {errors.viewings && (
                      <div className={styles.textDanger}>
                        {errors.viewings}
                      </div>
                    )}
                  </div>
                </div>

                <div className={styles.controls}>
                  {formData.viewings.length === 0 && (
                    <div className={styles.noViewingsMessage}>
                      <p>No viewings added yet. Search for a property above to add viewings.</p>
                    </div>
                  )}
                  {formData.viewings.map((viewing, index) => (
                    <div key={index} className={styles.viewingRow}>
                      <input
                        name={`viewings[${index}][property_id]`}
                        type="hidden"
                        value={viewing.property_id}
                      />
                      <input
                        name={`viewings[${index}][property_name]`}
                        type="hidden"
                        value={viewing.property_name}
                      />

                      <div className={styles.viewingDelete}>
                        <button
                          className={styles.btnRemove}
                          type="button"
                          onClick={() => removeViewing(index)}
                        >
                          Delete
                        </button>
                      </div>
                      <div className={styles.viewingProperty}>
                        <span className={styles.propertyName}>{viewing.property_name}</span>
                      </div>
                      <div className={styles.viewingDay}>
                        <label className={`${styles.viewingLabel} ${styles.labelRequired}`}>
                          Day
                        </label>
                        <input
                          className={styles.viewingInput}
                          required="required"
                          name={`viewings[${index}][day]`}
                          value={viewing.day}
                          onChange={(e) =>
                            handleViewingChange(
                              index,
                              "day",
                              e.target.value
                            )
                          }
                          type="date"
                          id={`viewings[${index}][day]`}
                        />
                        {errors[`viewings.${index}.day`] && (
                          <div className={styles.textDanger}>
                            {errors[`viewings.${index}.day`]}
                          </div>
                        )}
                      </div>
                      <div className={styles.viewingFrom}>
                        <label className={`${styles.viewingLabel} ${styles.labelRequired}`}>
                          From
                        </label>
                        <input
                          className={styles.viewingInput}
                          required="required"
                          name={`viewings[${index}][time_from]`}
                          value={viewing.time_from}
                          onChange={(e) =>
                            handleViewingChange(
                              index,
                              "time_from",
                              e.target.value
                            )
                          }
                          type="time"
                          min="09:00"
                          id={`viewings[${index}][time_from]`}
                        />
                        {errors[`viewings.${index}.time_from`] && (
                          <div className={styles.textDanger}>
                            {errors[`viewings.${index}.time_from`]}
                          </div>
                        )}
                      </div>
                      <div className={styles.viewingTo}>
                        <label className={styles.viewingLabel}>
                          To
                        </label>
                        <input
                          className={styles.viewingInput}
                          name={`viewings[${index}][time_to]`}
                          value={viewing.time_to}
                          onChange={(e) =>
                            handleViewingChange(
                              index,
                              "time_to",
                              e.target.value
                            )
                          }
                          type="time"
                          id={`viewings[${index}][time_to]`}
                        />
                        {errors[`viewings.${index}.time_to`] && (
                          <div className={styles.textDanger}>
                            {errors[`viewings.${index}.time_to`]}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </fieldset>

              <fieldset className={styles.formHorizontal}>
                <div className={styles.formGroupTextRight}>
                  <div className={styles.colSm12}>
                    <button
                      type="submit"
                      className={`${styles.btn} ${styles.btnHead} ${styles.btnStandard}`}
                      disabled={submitting}
                    >
                      {submitting ? "Creating..." : "Create"}
                    </button>
                    <p>
                      The viewing will not be created if the property is not
                      ACTIVE.
                    </p>
                  </div>
                </div>
              </fieldset>
            </form>

            {/* Success/Error Messages */}
            {success && <div className={styles.alertSuccess}>{success}</div>}
            {error && <div className={styles.alertDanger}>{error}</div>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PropertyViewingsCreate;
